import {Component, Input, OnInit} from '@angular/core';
import {ParkInterface} from '@app/app/shared/interfaces/park.interface';
import * as momentTimezone from 'moment-timezone';
import {NgxSmartModalService} from 'ngx-smart-modal';
import {CommandsService} from '@app/app/shared/services/commands.service';
import {PercentsPipe} from '@app/app/shared/helpers/percents.pipe';
import {DecimalPipe} from '@angular/common';
import {AuthenticationService} from '@app/app/shared/services/authentication.service';

@Component({
  templateUrl: './park.html',
  selector: 'park'
})

export class ParkComponent {
  @Input() park: ParkInterface;
  // Helper variables
  public today = Date.now();
  objectKeys = Object.keys;
  public isPro: boolean;
  public isPremium: boolean;

  constructor(public ngxSmartModalService: NgxSmartModalService, private comm: CommandsService,
              private percentsPipe: PercentsPipe, private decimalPipe: DecimalPipe,
              private auth: AuthenticationService) {
    this.isPro = this.auth.isPro();
    this.isPremium = this.auth.isPremium();
  }

  // Helper functions
  public inOperationHours() {
    return this.currentSchedule() !== null;
  }

  public stopped() {
    if (!this.isDefined(this.park) || !this.isDefined(this.park.attributes.shadow) || !this.isDefined(this.park.attributes.shadow.splashpad)) {
      return false;
    }

    return this.park.attributes.shadow.splashpad.mode === 'off';
  }

  public filters(): any[] {
    const f = [];

    if (this.isDefined(this.park) && this.isDefined(this.park.attributes.shadow) && this.isDefined(this.park.attributes.shadow.data)
      && this.isDefined(this.park.attributes.shadow.data.filters) && this.isDefined(this.park.attributes.shadow.filters)) {

      const filters = this.park.attributes.shadow.filters;
      const dirtLevels = this.park.attributes.shadow.data.filters;

      Object.keys(dirtLevels).forEach(filterId => {
        Object.keys(filters).forEach(fId => {
          if (filterId === fId) {
            f.push({type: filters[fId].type, value: dirtLevels[filterId].wearPercent});
          }
        });
      });
    }

    return f;
  }

  public filterNames(): string[] {
    return this.filters().map(f => f.type);
  }

  public filterValues() {
    return this.filters().map(f => {
      if (typeof f.value === 'number') {
        return this.percentsPipe.transform(this.decimalPipe.transform(100 - f.value, '1.0-1'));
      } else {
        return 'N/A';
      }
    });
  }

  public inSequence() {
    if (!this.isDefined(this.park) || !this.isDefined(this.park.attributes.shadow) || !this.isDefined(this.park.attributes.shadow.sequence)) {
      return false;
    }

    return this.park.attributes.shadow.sequence.mode === 'sequence_playing';
  }

  public currentAlarms(): any[] {
    const alarms = [];

    Object.keys(this.park.attributes.notifications).forEach((key) => {
      if (this.park.attributes.notifications[key].type === 'alarm' && this.park.attributes.notifications[key].status === 'pending') {
        alarms.push(this.park.attributes.notifications[key]);
      }
    });

    return alarms;
  }

  public marqueeClass() {
    const alarms = this.currentAlarms();

    if (alarms.length <= 3) {
      return 'marquee';
    } else if (alarms.length <= 6) {
      return 'marquee-30';
    } else if (alarms.length <= 9) {
      return 'marquee-45';
    } else if (alarms.length <= 12) {
      return 'marquee-60';
    } else if (alarms.length <= 15) {
      return 'marquee-75';
    } else if (alarms.length <= 18) {
      return 'marquee-90';
    } else if (alarms.length <= 21) {
      return 'marquee-105';
    } else {
      return 'marquee-120';
    }
  }

  public solvedAlarms(): any[] {
    const alarms = [];

    Object.keys(this.park.attributes.notifications).forEach((key) => {
      if (this.park.attributes.notifications[key].type === 'alarm' && this.park.attributes.notifications[key].status === 'solved') {
        alarms.push(this.park.attributes.notifications[key]);
      }
    });

    return alarms;
  }

  public isDefined(val) {
    return typeof (val) !== 'undefined';
  }

  protected getParkTz(park) {
    return ((park.attributes || {}).location || {}).timezone || 'UTC';
  }

  public waterTemp(tempData) {
    if (typeof tempData === 'string' || typeof tempData === 'number') {
      return tempData;
    }

    if (typeof tempData === 'object' && Object.values(tempData).length > 0) {
      const sum = Object.values(tempData).reduce((a, b) => a + b, 0);
      return (sum / Object.values(tempData).length) || 0;
    }

    return null;
  }

  public acid(chemicals) {
    if (chemicals && chemicals.hasOwnProperty('acid percent')) {
      return chemicals['acid percent'] === -1 ? null : chemicals['acid percent'];
    }

    return null;
  }

  public chlorine(chemicals) {
    if (chemicals && chemicals.hasOwnProperty('chlorine percent')) {
      return chemicals['chlorine percent'] === -1 ? null : chemicals['chlorine percent'];
    }

    return null;
  }

  public ph(chemicals) {
    if (chemicals && chemicals.hasOwnProperty('ph')) {
      return chemicals['ph'] === -1 ? null : '' + chemicals['ph'];
    }

    return null;
  }

  public orp(chemicals) {
    if (chemicals && chemicals.hasOwnProperty('orp')) {
      return chemicals['orp'] === -1 ? null : '' + chemicals['orp'];
    }

    return null;
  }

  public currentSchedule() {
    let currSc = null;

    if (!this.isDefined(this.park) || !this.isDefined(this.park.attributes.shadow) || !this.isDefined(this.park.attributes.shadow.schedule)) {
      return currSc;
    }

    const timezone = this.getParkTz(this.park);

    Object.keys(this.park.attributes.shadow.schedule).forEach(schedule_id => {
      const schedule = this.park.attributes.shadow.schedule[schedule_id];
      const tz = momentTimezone.tz.zone(timezone).abbr(new Date().getTime());
      const scheduleStart = momentTimezone.tz(schedule.begin, 'HH:mm', timezone);
      const scheduleEnd = momentTimezone.tz(schedule.end, 'HH:mm', timezone);

      if (this.isToday(schedule, timezone) && this.isNow(scheduleStart, scheduleEnd)) {
        currSc = `${scheduleStart.format(this.scheduleTimeFormat())} - ${scheduleEnd.format(this.scheduleTimeFormat())} ${tz}`;
      }
    });

    return currSc;
  }

  protected isToday(schedule, timezone) {
    const currentTimeInParkTimezone = momentTimezone.tz(timezone || 'UTC');

    const startTime = momentTimezone.tz(schedule.begin, 'HH:mm', timezone || 'UTC');
    const endTime = momentTimezone.tz(schedule.end, 'HH:mm', timezone || 'UTC');

    if (!startTime.isValid() || !endTime.isValid()) {
      return false;
    }

    const checkYesterday = !startTime.isBefore(endTime);

    let today = null;

    if (checkYesterday) {
      today = currentTimeInParkTimezone.subtract(1, 'days');
    } else {
      today = currentTimeInParkTimezone;
    }

    if (schedule.hasOwnProperty('date') && schedule.date !== null && schedule.date.length > 0) {
      // One-time schedule
      const date = momentTimezone.tz(schedule.date, 'MM-DD', timezone || 'UTC');

      return today.isSame(date, 'day');
    }

    if (schedule.hasOwnProperty('days') && schedule.days !== null && schedule.days.length > 0) {
      // Weekly schedule

      const daysOfWeek = schedule.days;

      const dayOfWeek = 1 + parseInt(today.format('d'), 10);

      return daysOfWeek.includes(dayOfWeek);
    }

    return false;
  }

  protected isNow(scheduleStart, scheduleEnd): boolean {
    const currentMoment = momentTimezone();

    return currentMoment.isBetween(scheduleStart, scheduleEnd);
  }

  public formatAlarmTimestamp(ts) {
    const timezone = this.getParkTz(this.park);
    const tz = momentTimezone.tz.zone(timezone).abbr(new Date().getTime());

    const alarmTimestamp = momentTimezone.tz(Number(ts), 'UTC');

    return alarmTimestamp.format(`YYYY-MM-DD ${this.scheduleTimeFormat()}`) + ' ' + tz;
  }

  protected scheduleTimeFormat() {
    const isAmPm = true;

    if (isAmPm) {
      return 'hh:mmA';
    } else {
      return 'HH:mm';
    }
  }

  public otherSchedules() {
    const otherSchedules = [];

    if (!this.isDefined(this.park) || !this.isDefined(this.park.attributes.shadow) || !this.isDefined(this.park.attributes.shadow.schedule)) {
      return otherSchedules;
    }

    const timezone = this.getParkTz(this.park);

    Object.keys(this.park.attributes.shadow.schedule).forEach(scheduleId => {
      const schedule = this.park.attributes.shadow.schedule[scheduleId];

      if (schedule.hasOwnProperty('begin') && schedule.hasOwnProperty('end')) {
        const tz = momentTimezone.tz.zone(timezone).abbr(new Date().getTime());
        const scheduleStart = momentTimezone.tz(schedule.begin, 'HH:mm', timezone);
        const scheduleEnd = momentTimezone.tz(schedule.end, 'HH:mm', timezone);

        if (!this.isToday(schedule, timezone) || !this.isNow(scheduleStart, scheduleEnd)) {
          otherSchedules.push([
            `${scheduleStart.format(this.scheduleTimeFormat())} - ${scheduleEnd.format(this.scheduleTimeFormat())} ${tz}`,
            parseInt(schedule.begin.replace(':', '') || 0, 10)
          ]);
        }
      }
    });

    otherSchedules.sort((a, b) => {
      return a[1] - b[1];
    });

    return otherSchedules;
  }

  public isOnline(splashpad) {
    if (!splashpad.hasOwnProperty('lastConnection')) {
      return false;
    }

    if (!splashpad.hasOwnProperty('lastDisconnection')) {
      return true;
    }

    return Number.parseInt(splashpad.lastConnection) > Number.parseInt(splashpad.lastDisconnection);
  }

  public isOffline(splashpad) {
    if (!splashpad.hasOwnProperty('lastConnection')) {
      return true;
    }

    if (!splashpad.hasOwnProperty('lastDisconnection')) {
      return false;
    }

    return Number.parseInt(splashpad.lastConnection) <= Number.parseInt(splashpad.lastDisconnection);
  }

  public usageRate() {
    if (!this.isDefined(this.park) || !this.isDefined(this.park.attributes.shadow)
      || !this.isDefined(this.park.attributes.shadow.schedule) || !this.isDefined(this.park.attributes.shadow.stats)) {
      return;
    }

    const activations = this.park.attributes.shadow.stats.activations.withSequence; // number of seq activations during current day
    const lastActivation = new Date(this.park.attributes.shadow.stats.activations.last * 1000);
    const ur = [];
    const current = new Date();
    const cd = current.getDate();
    const cm = current.getMonth() + 1;
    const cy = current.getFullYear();

    if (lastActivation >= current) {
      Object.keys(this.park.attributes.shadow.schedule).forEach(schedule_id => {
        const schedule = this.park.attributes.shadow.schedule[schedule_id];

        const begin = new Date(cy + '-' + this.checkTime(cm) + '-' + this.checkTime(cd) + 'T' + schedule.begin + ':00');
        const end = new Date(cy + '-' + this.checkTime(cm) + '-' + this.checkTime(cm) + 'T' + schedule.end + ':00');
        const timeNow = this.checkTime(current.getHours()) + ':' + this.checkTime(current.getMinutes());
        let hours = 0;
        let minutes = 0;
        let hour = 0;

        if (schedule.begin <= timeNow && schedule.end > timeNow) {
          hours = current.getHours() - begin.getHours();
          minutes = current.getMinutes() - begin.getMinutes();
        } else {
          hours = end.getHours() - begin.getHours();
          minutes = end.getMinutes() - begin.getMinutes();
        }

        hour += (hours + (minutes / 60));

        ur.push(((schedule.duration / 60) * activations) / hour);
      });

      return Math.max(...ur);
    }

    return 0;
  }

  public reloadPark() {
    this.comm.getShadow(this.park.attributes.parkId, this.park.id).then((res) => {
      // this.isSendingCommand = false;
    }).catch((err) => {
      // this.isSendingCommand = false;
    });
  }

  protected checkTime(i) {
    return (i < 10) ? '0' + i : i;
  }

  public alarms() {
    if (this.isDefined(this.park) && this.isDefined(this.park.attributes.shadow) && this.isDefined(this.park.attributes.shadow.alarms)) {
      return this.park.attributes.shadow.alarms;
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/dashboard/parks/park/park.component.ts